<script setup lang="ts">
import { usePublicSiteRepository } from '~/repositories/publicSiteRepository'

// Tenant hosts render the themed public site (SiteShell owns its own
// chrome); the platform domain uses the marketing layout, same as
// /pricing and the vertical landing pages.
definePageMeta({ layout: false, middleware: ['tenant'] })

const tenant = useTenant()
const auth = useAuth()
const localePath = useLocalePath()
const publicSiteRepo = usePublicSiteRepository()

// On a tenant subdomain, route logged-in staff straight to /agent so they
// don't sit on the marketing page. Anonymous + non-staff visitors see the
// tenant landing.
if (import.meta.client) {
  if (!auth.user.value) await auth.init()

  if (tenant.tenantMode.value !== 'platform' && auth.user.value) {
    await tenant.checkMembership()
    const role = tenant.membership.value?.role
    if (role === 'admin' || role === 'moderator') {
      await navigateTo(localePath('/agent'))
    }
  }
}

const activeTenant = computed(() => tenant.tenant.value)
const isTenantHost = computed(() => tenant.tenantMode.value !== 'platform')

// A tenant can pick a block-built page (via /agent/site) as their home page
// instead of the structured landing sections. Falls back to SiteHome below
// if unset, or if the page was since unpublished/deleted out from under it.
const homePageSlug = computed(() => activeTenant.value?.landing.home_page_slug ?? null)
const { data: homePageRes, error: homePageError } = await useAsyncData(
  'site-home-page',
  () => homePageSlug.value ? publicSiteRepo.getPage(homePageSlug.value) : Promise.resolve(null),
  { watch: [homePageSlug] }
)
const homePage = computed(() => homePageError.value ? null : (homePageRes.value?.data ?? null))

const seoTitle = computed(() => {
  if (homePage.value) return homePage.value.seo.meta_title || homePage.value.title
  return activeTenant.value
    ? (activeTenant.value.landing.meta_title || activeTenant.value.name)
    : 'agency-saas'
})
const seoDescription = computed(() => {
  if (homePage.value) {
    return homePage.value.seo.meta_description
      || activeTenant.value?.landing.hero_subheadline
      || activeTenant.value?.description
      || ''
  }
  return activeTenant.value
    ? (activeTenant.value.landing.meta_description
      || activeTenant.value.landing.hero_subheadline
      || activeTenant.value.description
      || '')
    : 'Case management, document workflows, and a magic-link client portal for visa, translation, and education agencies.'
})
const seoImage = computed(() =>
  activeTenant.value?.landing.og_image_url
  || activeTenant.value?.branding?.banner_url
  || activeTenant.value?.branding?.logo_url
  || null
)

useSeoMeta({
  title: () => seoTitle.value,
  description: () => seoDescription.value,
  ogTitle: () => seoTitle.value,
  ogDescription: () => seoDescription.value,
  ogImage: () => seoImage.value,
  ogType: 'website'
})
</script>

<template>
  <SiteShell
    v-if="isTenantHost && activeTenant"
    :tenant="activeTenant"
  >
    <template v-if="homePage">
      <article>
        <SiteBlockRenderer :blocks="homePage.blocks" />
      </article>
      <LandingSectionsContactBlock :tenant-name="activeTenant.name" />
      <LandingSectionsSocials :landing="activeTenant.landing" />
    </template>
    <SiteHome
      v-else
      :tenant="activeTenant"
    />
  </SiteShell>
  <NuxtLayout
    v-else
    name="marketing"
  >
    <LandingPlatformLanding />
  </NuxtLayout>
</template>
